import React, {useEffect, useRef, useState} from 'react';
import useImage from "use-image";
import {Modal} from "../modal/modal.jsx";
import ImageEditorToolbar from "./components/image-editor-toolbar.jsx";
import ImageEditorUtils from "./utils/image-editor-utils.js";
import Cropping from "./components/cropping.jsx";
import ShapeRender from "./components/shape-render.jsx";
import {Layer, Stage, Transformer, Image} from "react-konva";
import Button from "../button/button.jsx";
import projectUtils from "../../utils/project-utils.js";
import DrawAction from "./enums/draw-action.js";
import "./css/image-editor.css"

const ImageEditorModal = ({file, setShow, show, save}) => {
    const [size, setSize] = useState({width: 0, height: 0});

    const [imageSize, setImageSize] = useState({width: 0, height: 0});
    const [imageUrl, setImageUrl] = useState('');
    const [isCropping, setIsCropping] = useState(false);
    const [cropSize, setCropSize] = useState();
    const [drawAction, setDrawAction] = useState(null);
    const [shapes, setShapes] = useState([]);
    const [currentShape, setCurrentShape] = useState(undefined)

    const [transformerProps, setTransformerProps] = useState({})

    const [strokeSize, setStrokeSize] = useState(4);
    const [fontSize, setFontSize] = useState(18);
    const [strokeColor, setStrokeColor] = useState("#000000")

    const imageUrlRef = useRef([]);

    const imageRef = useRef(undefined);
    const stageRef = useRef(undefined);
    const isPaintRef = useRef(false);
    const cropSizeRef = useRef(undefined);
    const fistLayerRef = useRef(undefined);
    const transformerRef = useRef(undefined);

    const [image] = useImage(imageUrl, 'anonymous');

    useEffect(() => {
        const listener = (e) => {
            if (e.key === "Delete") removeShape(currentShape)
        }

        document.removeEventListener("keydown", listener)

        if (!currentShape) return;

        document.addEventListener("keydown", listener)

        return () => document.removeEventListener("keydown", listener)
    }, [currentShape]);

    useEffect(() => {
        if (!file) return;

        const newUrl = URL.createObjectURL(file);

        imageUrlRef.current = [];

        transformerRef?.current?.nodes([]);
        setCurrentShape(undefined);
        setShapes([]);
        setCropSize(undefined);
        setIsCropping(false);
        setDrawAction(null);

        isPaintRef.ccurrent = undefined;
        cropSizeRef.current = undefined;

        imageUrlRef.current.push(newUrl);
        setImageUrl(newUrl);

        return () => URL.revokeObjectURL(newUrl);
    }, [file]);

    useEffect(() => {
        if (image && size.width) {
            const {naturalWidth, naturalHeight} = image;
            const ratio = naturalWidth / naturalHeight;

            let newWidth = naturalWidth;
            let newHeight = naturalHeight;

            const maxWidth = size.width;
            const maxHeight = size.height;

            if (newWidth > maxWidth) {
                newWidth = maxWidth;
                newHeight = newWidth / ratio;
            }

            if (newHeight > maxHeight) {
                newHeight = maxHeight;
                newWidth = newHeight * ratio;
            }

            setImageSize({width: newWidth, height: newHeight});
        }
    }, [image, size]);

    useEffect(() => {
        const shape = shapes.find(el => el.id === currentShape);
        const isTextAction = shape?.type === DrawAction.TEXT;

        setTransformerProps({
            enabledAnchors: isTextAction ? ['middle-left', 'middle-right'] : undefined,
            boundBoxFunc: isTextAction
                ? (oldBox, newBox) => ({
                    ...newBox,
                    width: Math.max(30, newBox.width),
                })
                : (oldBox, newBox) => {
                    if (newBox.width < 20 || newBox.height < 20) {
                        return oldBox;
                    }
                    return newBox;
                }
        });
    }, [currentShape]);

    const reload = () => {
        projectUtils.swalMessage(
            'warning',
            'Atenção',
            "Ao confirmar, as alterações serão perdidas. \n Deseja continuar?",
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (!isConfirmed) return;

                setImageUrl(imageUrlRef.current[0]);
                transformerRef?.current?.nodes([]);
                setCurrentShape(undefined);
                setShapes([]);
            }
        )
    }

    const handleSave = () => {
        transformerRef?.current?.nodes([]);

        save(fistLayerRef.current.toDataURL());
        setShow(false);
    }

    const applyCrop = () => {
        if (fistLayerRef.current) {
            const options = {
                x: cropSize?.x,
                y: cropSize?.y,
                width: cropSize?.width,
                height: cropSize?.height,
            }

            const dataURL = fistLayerRef.current.toDataURL(options);
            imageUrlRef.current.push(dataURL);
            setImageUrl(dataURL);
            setShapes([]);

            cropSizeRef.current = options;
            setIsCropping(false);
        }
    }

    const onSelectAction = (action) => {
        setDrawAction(prev => prev !== action ? action : null);
        transformerRef?.current?.nodes([]);
        setCurrentShape(undefined);
    }

    const handleCrop = () => {
        onSelectAction(null);

        setImageUrl(isCropping ? imageUrlRef.current.at(-1) : imageUrlRef.current[0]);
        setIsCropping(!isCropping);
    }

    const removeShape = () => {
        if (!currentShape) return;

        setShapes(prev => prev.filter(item => item.id !== currentShape))
        transformerRef?.current?.nodes([]);
        setCurrentShape(undefined);
    }

    return (
        <Modal open={show} setOpen={setShow} title={"Editor de imagens"}>
            <Modal.Body>
                <div className="flex justify-center items-center flex-col gap-2">
                    <ImageEditorToolbar
                        activeAction={drawAction}
                        onSelectAction={onSelectAction}
                        reload={reload}
                        handleCrop={handleCrop}
                        isCropping={isCropping}
                        strokeColor={strokeColor}
                        strokeSize={strokeSize}
                        fontSize={fontSize}
                        currentShape={currentShape}
                        removeShape={removeShape}
                        handleStrokeSize={(size) => ImageEditorUtils.handleStrokeSize(size, setShapes, setStrokeSize, transformerRef)}
                        handleFontSize={(size) => ImageEditorUtils.handleFontSize(size, setShapes, setFontSize, transformerRef)}
                        handleStrokeColor={(color) => ImageEditorUtils.handleStrokeColor(color, setShapes, setStrokeColor, transformerRef)}
                    />
                    <div className="image-editor-wrapper" ref={(div) => {
                        if (!div || size.height) return

                        setSize({
                            width: div.offsetWidth, height: div.offsetHeight,
                        });
                    }}>
                        <Stage
                            width={imageSize.width}
                            height={imageSize.height}
                            ref={stageRef}
                            onMouseDown={() => ImageEditorUtils.onStageMouseDown(drawAction, stageRef, transformerRef, currentShape, setCurrentShape, isPaintRef, setShapes, strokeSize, strokeColor, fontSize, isCropping)}
                            onMouseMove={() => ImageEditorUtils.onStageMouseMove(drawAction, stageRef, isPaintRef, setShapes, currentShape)}
                            onMouseUp={() => ImageEditorUtils.onStageMouseUp(isPaintRef, stageRef, currentShape, transformerRef, setShapes, drawAction, setDrawAction, isCropping)}
                        >
                            <Layer ref={fistLayerRef}>
                                <Image
                                    ref={imageRef}
                                    image={image}
                                    name={"image"}
                                    width={imageSize.width}
                                    height={imageSize.height}
                                    x={0}
                                    y={0}
                                />

                                <ShapeRender
                                    shapes={shapes}
                                    onShapeClick={(el) => ImageEditorUtils.handleShapeClick(el, drawAction, setCurrentShape, transformerRef, shapes, setStrokeColor, setStrokeSize, setFontSize)}
                                    drawAction={drawAction}
                                    currentShape={currentShape}
                                    stageRef={stageRef}
                                    setShapes={setShapes}
                                    onShapeMove={(id, x, y) => ImageEditorUtils.updateShapePosition(id, x, y, setShapes)}
                                    onTextChange={(id, text) => ImageEditorUtils.updateShapesText(id, text, setShapes)}
                                />

                                <Transformer
                                    ref={transformerRef}
                                    {...transformerProps}
                                />
                            </Layer>

                            {
                                isCropping &&
                                <Cropping
                                    cropSize={cropSize}
                                    setCropSize={setCropSize}
                                    cropSizeRef={cropSizeRef}
                                    imageSize={imageSize}
                                    imageRef={imageRef}
                                    image={image}
                                />
                            }
                        </Stage>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {
                    !isCropping &&
                    <Button target='primary' onClick={handleSave}>Salvar</Button>
                }

                {
                    isCropping &&
                    <>
                        <Button target="warning" onClick={handleCrop}>Não aplicar</Button>
                        <Button target="secondary" onClick={applyCrop}>Aplicar</Button>
                    </>
                }
            </Modal.Footer>
        </Modal>
    );
};

export default ImageEditorModal;